import { Icon } from '@buggy/shared'
import { Button, Divider, Tooltip } from 'antd'
import { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import AddToCollectionButton, {
  TriggerElement,
} from '@/components/collection/add-to-collection-button'
import KolExport from '@/components/kol/export/kol-export'
import KolActionsDropdown from '@/components/kol/navigation/kol-actions-dropdown'
import { PlatformData } from '@/components/kol/navigation/platform-select'
import useIrmContentContext from '@/components/layout/hooks/use-irm-content-context'
import { createTourStepData } from '@/components/tour/utils'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'

interface QuickActionsProps {
  isDisabled: boolean
  kolInfo: KolSchemaModel
  platformData: PlatformData
}

const QuickActions: FunctionComponent<QuickActionsProps> = ({
  isDisabled,
  kolInfo,
  platformData,
}) => {
  const { formatMessage } = useIntl()
  const irmContentContext = useIrmContentContext()

  const [isCollectionKol, setIsCollectionKol] = useState(
    kolInfo.isCollectionKol,
  )
  const { withKolPermission } = usePermissions()

  return (
    <Wrapper>
      <Divider type='vertical' />
      <Actions>
        {kolInfo.visibility && !withKolPermission && (
          <AddToCollectionButton
            containerRef={irmContentContext?.containerRef}
            from='KOL Detail'
            isDisabled={isDisabled}
            kol={kolInfo}
            platform={platformData.shortcode}
            tooltip={{
              placement: 'bottom',
              title: formatMessage({
                id: isCollectionKol
                  ? 'general:is_collected'
                  : 'project:collection_popup_title',
              }),
            }}
            onCollectedChange={(_kolId, isCollected): void =>
              setIsCollectionKol(isCollected)
            }
          />
        )}
        <Tooltip
          placement='bottom'
          title={formatMessage(
            { id: 'kol:open_platform_link' },
            { platformTitle: platformData.title },
          )}
        >
          <PlatformLink
            disabled={isDisabled}
            href={platformData.url}
            icon={<Icon name='external-link' />}
            rel='noopener'
            target='_blank'
            onClick={(): void => {
              ampli.visitKolSocialMedia({
                platform: platformData.shortcode,
                from: 'KD button',
              })
            }}
          />
        </Tooltip>
        <Tooltip
          placement='bottom'
          title={formatMessage({ id: 'kol:export_modal_title' })}
        >
          <div {...createTourStepData('social-performance', 4)}>
            <KolExport
              isDisabled={isDisabled}
              kolDcIdHashed={kolInfo.dcIdHashed}
              kolId={kolInfo.id}
            />
          </div>
        </Tooltip>
        <KolActionsDropdown
          actions={[
            'viewCooperationDetail',
            'addKolToPredictiveReport',
            'addKolToCampaign',
            'addKolToReport',
            'removeFromDirectory',
          ]}
          from='KOL Detail'
          isDisabled={isDisabled}
          kolInfo={kolInfo}
        />
      </Actions>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  height: 60px;

  .ant-divider {
    margin: 0 20px 0;
    height: 100%;
  }
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;

  > :not(:last-child) {
    margin: 0 5px 0 0;
  }

  ${TriggerElement} span {
    padding: 10px;

    svg {
      font-size: 18px;
    }
  }
`

const PlatformLink = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
`

export default QuickActions
